import React from "react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Typography } from "antd";
import { differenceInYears, isBefore, subYears } from "date-fns";
import { phoneNumberLengthRegEx } from "../../../../utils/formValidation";

const zipInputRules = [
  {
    pattern: /^\d{5}$/,
    message: ""
  }
];

const onlyWhitespacesRule = {
  whitespace: true,
  message: ""
};

const emailInputRules = [
  onlyWhitespacesRule,
  {
    type: "email",
    message: ""
  }
];

const npiInputRule = {
  pattern: /^\d{10}$/,
  message: ""
};

const phoneNumberLengthRule = {
  pattern: phoneNumberLengthRegEx,
  message: ""
};

const isUnderEighteen = (dob) => {
  const dobDate = new Date(dob);
  const today = new Date();

  const age = differenceInYears(today, dobDate);
  return age < 18 || (age === 18 && isBefore(today, subYears(dobDate, 18)));
};

const noFutureDateRule = ({ message } = {}) => ({
  validator: (rule, value) => {
    if (value && isBefore(new Date(), new Date(value))) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const patientMinAgeRule = ({ minAge, message }) => ({
  validator: (rule, value) => {
    if (!value) return Promise.resolve();
    const age = differenceInYears(new Date(), new Date(value));
    if (age < minAge) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const patientCantBeUnderEighteenRule = ({ message }) => ({
  validator: (rule, value) => {
    if (isUnderEighteen(value)) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const mustMatchAllowedValues = ({ allowedValues, message }) => ({
  validator: (rule, value) => {
    if (!allowedValues.includes(value)) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const regexRule = ({ regex, message }) => ({
  pattern: regex,
  message: message
});

const lessOrEqualRule = (t, max, fieldName) => ({
  max: max,
  message: t("errorMessages.should_be_less_than_or_equal", {
    fieldName: fieldName,
    maxCharacter: max
  })
});

// JSON-form max-length rule: a single-params factory so renderReactFromJson can resolve it
// from a field's `rules`/`rulesParams` (the engine calls factory rules with one params object).
const maxLengthRule = ({ max, message }) => ({
  max,
  message
});

const PAYER_NAME_MAX_LENGTH = 50;

// no special characters rule preventing all special characters except hyphens and underscores
const noSpecialCharactersRule = {
  pattern: /^[a-zA-Z0-9\s\-_]*$/,
  message: "Special characters are not allowed"
};
const minNumberValidation =
  ({ minNumber }) =>
  () => ({
    validator(_, value) {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return Promise.reject();
      }
      if (numValue < minNumber) {
        return Promise.reject();
      }
      return Promise.resolve();
    }
  });

export {
  zipInputRules,
  npiInputRule,
  emailInputRules,
  onlyWhitespacesRule,
  phoneNumberLengthRule,
  noFutureDateRule,
  patientMinAgeRule,
  patientCantBeUnderEighteenRule,
  mustMatchAllowedValues,
  regexRule,
  lessOrEqualRule,
  maxLengthRule,
  PAYER_NAME_MAX_LENGTH,
  noSpecialCharactersRule,
  minNumberValidation
};
